/**
 * WebAuthn client — wraps the register/login ceremonies. The Laravel side is
 * asbiin/laravel-webauthn under /api/webauthn/*. Request/response shapes match
 * the standard PublicKeyCredential JSON.
 */
import { api } from "./api";

export function isPasskeySupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;
}

function b64urlToBuf(b64url: string): ArrayBuffer {
  let s = String(b64url).replace(/-/g, "+").replace(/_/g, "/");
  if (s.length % 4) s += "===".slice(0, 4 - (s.length % 4));
  const bin = atob(s);
  const out = new ArrayBuffer(bin.length);
  const view = new Uint8Array(out);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return out;
}

function bufToB64url(buf: ArrayBuffer): string {
  const u8 = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------- Registration ----------

interface CreationOptionsJson {
  publicKey?: PublicKeyCredentialCreationOptionsJSON;
  challenge?: string;
  rp?: { id?: string; name: string };
  user?: { id: string; name: string; displayName: string };
  pubKeyCredParams?: Array<{ type: "public-key"; alg: number }>;
  excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  authenticatorSelection?: PublicKeyCredentialCreationOptions["authenticatorSelection"];
  attestation?: PublicKeyCredentialCreationOptions["attestation"];
  timeout?: number;
}

interface PublicKeyCredentialCreationOptionsJSON {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  authenticatorSelection?: PublicKeyCredentialCreationOptions["authenticatorSelection"];
  attestation?: PublicKeyCredentialCreationOptions["attestation"];
  timeout?: number;
}

/** Register a new passkey for the currently signed-in user. */
export async function registerPasskey(name: string): Promise<void> {
  if (!isPasskeySupported()) throw new Error("This browser doesn't support passkeys.");

  const raw = await api<CreationOptionsJson>("/api/webauthn/keys/options", {
    method: "POST",
    body: {},
  });
  const pk = (raw.publicKey ?? raw) as PublicKeyCredentialCreationOptionsJSON;

  const options: PublicKeyCredentialCreationOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    user: {
      ...pk.user,
      id: b64urlToBuf(pk.user.id),
    },
    excludeCredentials: (pk.excludeCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };

  const cred = (await navigator.credentials.create({ publicKey: options })) as PublicKeyCredential | null;
  if (!cred) throw new Error("Passkey creation cancelled.");
  const response = cred.response as AuthenticatorAttestationResponse;

  await api("/api/webauthn/keys", {
    method: "POST",
    body: {
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        attestationObject: bufToB64url(response.attestationObject),
        clientDataJSON: bufToB64url(response.clientDataJSON),
      },
      name,
    },
  });
}

// ---------- Login ----------

interface RequestOptionsJson {
  publicKey?: PublicKeyCredentialRequestOptionsJSON;
  challenge?: string;
  allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  rpId?: string;
  userVerification?: PublicKeyCredentialRequestOptions["userVerification"];
  timeout?: number;
}

interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  rpId?: string;
  userVerification?: PublicKeyCredentialRequestOptions["userVerification"];
  timeout?: number;
}

/** Sign in with a passkey. Resolves when the server sets the session cookie. */
export async function loginPasskey(email: string): Promise<void> {
  if (!isPasskeySupported()) throw new Error("This browser doesn't support passkeys.");

  const raw = await api<RequestOptionsJson>("/api/webauthn/auth/options", {
    method: "POST",
    body: { email },
  });
  const pk = (raw.publicKey ?? raw) as PublicKeyCredentialRequestOptionsJSON;

  const options: PublicKeyCredentialRequestOptions = {
    ...pk,
    challenge: b64urlToBuf(pk.challenge),
    allowCredentials: (pk.allowCredentials ?? []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };

  const cred = (await navigator.credentials.get({ publicKey: options })) as PublicKeyCredential | null;
  if (!cred) throw new Error("No credential returned.");
  const response = cred.response as AuthenticatorAssertionResponse;

  await api("/api/webauthn/auth", {
    method: "POST",
    body: {
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        authenticatorData: bufToB64url(response.authenticatorData),
        clientDataJSON: bufToB64url(response.clientDataJSON),
        signature: bufToB64url(response.signature),
        userHandle: response.userHandle ? bufToB64url(response.userHandle) : null,
      },
    },
  });
}

// ---------- List / delete ----------

export interface PasskeyRecord {
  id: number;
  name: string;
  created_at: string | null;
}

export const passkeyApi = {
  list: () => api<{ keys: PasskeyRecord[] }>("/api/passkeys"),
  destroy: (id: number) => api<{ ok: boolean }>(`/api/passkeys/${id}`, { method: "DELETE" }),
};
